import React, { useEffect, useState } from 'react';
import './RecentActivitiesWidget.css';
import { CategoryIcon, formatActivityTime, formatDuration, getCategoryInfo } from './activityShared';
import {
  getActivityStorageKey,
  getCurrentActivityUserId,
  loadStoredActivities,
  storeActivities,
  subscribeToActivityUpdates,
} from '../utils/activityStore';
import { fetchLatestActivities, sortActivitiesByNewest } from '../services/activityService';

function loadRecentItems(userId) {
  return sortActivitiesByNewest(loadStoredActivities(userId)).slice(0, 2);
}

export default function RecentActivitiesWidget() {
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    const userId = getCurrentActivityUserId();

    if (!userId) {
      setActivities([]);
      return undefined;
    }

    let cancelled = false;

    const loadFromStorage = () => {
      if (cancelled) return;

      const items = loadRecentItems(userId);
      setActivities(items);
    };

    const syncFromBackend = async () => {
      try {
        const latest = await fetchLatestActivities(2);

        if (cancelled) return;

        // Important:
        // [] is a valid successful backend response for a new user.
        // Do not fallback to localStorage when backend returns an empty array.
        if (Array.isArray(latest)) {
          const nextActivities = sortActivitiesByNewest(latest).slice(0, 2);

          setActivities(nextActivities);
          storeActivities(nextActivities, userId);
          return;
        }

        // Fallback only when backend returns an invalid/null response.
        loadFromStorage();
      } catch (e) {
        console.error('Failed to sync recent activities:', e);
        loadFromStorage();
      }
    };

    // Safe now, because localStorage is scoped by userId.
    loadFromStorage();

    // Then sync with backend.
    syncFromBackend();

    const unsubscribe = subscribeToActivityUpdates((event) => {
      const eventUserId = event?.detail?.userId;

      if (!eventUserId || eventUserId === userId) {
        loadFromStorage();
      }
    });

    const handleStorage = (e) => {
      if (e.key === getActivityStorageKey(userId)) {
        loadFromStorage();
      }
    };

    const handleFocus = () => {
      syncFromBackend();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleFocus);

    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  return (
    <div className="activity-widget-content">
      <div className="widget-header">
        <h3>Recent Activities</h3>
        <span className="view-all-link">View All</span>
      </div>

      <div className="widget-list">
        {activities.length === 0 ? (
      <div className="activity-mini-empty-state">
      <div className="activity-mini-empty-icon">
      <span>＋</span>
      </div>
        <div className="activity-mini-empty-text">
        <span className="activity-mini-empty-title">No activities yet</span>
        <span className="activity-mini-empty-subtitle">Add your first activity to see it here.</span>
      </div>
    </div>
      ) : (
          activities.map((activity) => {
            const categoryInfo = getCategoryInfo(activity.category);

            return (
              <div key={activity.id} className="activity-mini-item">
                <div
                  className="activity-mini-icon"
                  style={{ backgroundColor: `${categoryInfo.color}26` }}
                >
                  <CategoryIcon
                    id={categoryInfo.id}
                    size={20}
                    color={categoryInfo.color}
                  />
                </div>

                <div className="activity-mini-info">
                  <span className="activity-mini-title">
                    {categoryInfo.name}
                  </span>
                  <div className="activity-mini-meta">
                    <span>{formatActivityTime(activity.time)}</span>
                    <span className="dot">•</span>
                    <span>{formatDuration(activity.durationMin)}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}